import MomentUtils from '@mui/lab/AdapterMoment';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { Box, Button, InputLabel, TextField, Typography } from '@mui/material';
import { Loader } from 'components';
import ContentMultiple from 'components/ContentMultiple/ContentMultiple';
import InputDate from 'components/InputDate';
import Select, { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import SelectUser from 'components/SelectUser';
import useDeliverableService from 'features/Deliverables/hooks/useDeliverableService';
import {
  canEveryDeliverableHaveDateChange,
  isEveryDeliverableCustom,
  isEveryDeliverableNotFromQuote,
  isEveryDeliverableUnsubmitted,
} from 'features/Deliverables/services/deliverableService';
import useUserRoles from 'hooks/useUserRoles';
import { pickBy } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closeDrawerAction } from 'store/actions/drawerActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { selectConsultantsForTnM, selectPurchaseOrderForTnM } from 'store/selectors/deliverableSelectors';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import Scope from 'types/entities/Scope';
import User from 'types/entities/User';
import useApi from 'hooks/useApi';
import OrderApiService from 'api/OrderApiService';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { formatDateToddMMYYY } from 'helpers/format';

import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import { closeDialogAction, closeDialogWithDataAction } from 'store/actions/dialogsActions';
import {
  filterTotalFlagAction,
  multipleWorkunitSaveAction,
  selectBLDeliverablesAction,
} from 'store/actions/changeBaselineActions';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import useBaseLineOrderWorkunitApi from 'features/ChangeBaseline/ChangeBaselinePages/services/useBaseLineOrderWorkunitApi';
import PurchaseOrders from 'types/entities/PurchaseOrders';
import { LoadingButton } from '@mui/lab';
import { injectScopesAction } from 'store/actions/catalogToOrderActions';
// import { SearchParams } from 'api/ResourceAPI';
// import useTotalWorkunitService from '../Hooks/useTotalWorkunitService';

type DeliverableMultipleEditProps = {
  ids: OrderWorkunit['id'][];
};

// Define an interface for the object
interface WorkUnitData {
  workunit_desc?: string;
  workunit_name?: string;
  workunit_reference?: string;
}

const DeliverableMultipleEdit: React.FC<DeliverableMultipleEditProps> = ({ ids }) => {
  const { t } = useTranslation();
  const { isTnM } = useDeliverableService();
  // const { EditTotalBaseLineWorkunit } = useBaseLineOrderWorkunitApi();
  const [updateDateReason, setUpdateDateReason] = useState<string | null>(null);
  const [isChangeDateTextareaOpen, setIsChangeDateTextareaOpen] = useState(false);
  const { state, dispatch } = useAppState();
  const { dialogs } = state;
  const { fromPopup, data, type } = dialogs?.data;
  const { baseLineOrder, pageSize, totalworkunitData, multipleWorkunitSave } = useSelector(
    (state) => state.changeBaseline
  );
  const customers = useSelector((state) => state?.changeBaseline?.customers);
  const changeBaseline = useSelector((state) => state?.changeBaseline);
  const loadings = useSelector((state) => state.loadings);
  const [dateValid, setDateValid] = useState<boolean>(true);

  const userRoles = useUserRoles();
  const [scopes] = useState<Scope[]>(baseLineOrder?.tmscopes ?? []);
  const { makeCall } = useApi();
  const purchaseOrder = useSelector(selectPurchaseOrderForTnM);
  // const { handleBLTotalworkunitSearchParams } = useTotalWorkunitService();

  const tmAccountables = baseLineOrder?.tmaccountables;
  const tmConsultants = useSelector(selectConsultantsForTnM);
  const [accountables, setAccountables] = useState<User[]>(tmAccountables ?? []);
  const appFiltersSelected = useSelector((state) => state.filter?.selected);
  const consultants = isTnM() ? tmConsultants : baseLineOrder?.tmconsultants;
  const userLocale = navigator.language || 'fr-FR';
  const dateFormat = userLocale !== 'en-US' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

  const [paramsForEdition, setParamsForEdition] = useState<Partial<OrderWorkunit>>({});

  const [orderWorkunits, setOrderWorkunits] = useState<OrderWorkunit[]>([]);

  useEffect(() => {
    const getPoScopeByBaseline = async (currentOrder: any) => {
      const { purchase_orders, order_scopes } = await OrderApiService.fetchPoScopeBaseline(
        Number(baseLineOrder?.id),
        2,
        currentOrder?.affair?.otp as string
      );

      if (purchase_orders.length > 0) {
        purchase_orders.forEach(
          (po: { poend: string; poref: string; reference: string; postart: string; is_valid: boolean }) => {
            po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
          }
        );
      }

      dispatch(injectScopesAction(order_scopes?.datas));
      dispatch(getPurchaseOrderAction(purchase_orders));
    };
    getPoScopeByBaseline(baseLineOrder);
  }, []);

  const getSelectedDeliverableByListId = useCallback(
    async (deliverableItems: any) => {
      const deliverables = ids?.flatMap((item: any) => deliverableItems?.filter((val: any) => val?.id === item));
      if (deliverables?.length > 1 && !fromPopup) {
        const allClientsSame = deliverables.every((deliverable) => deliverable.client_id === deliverables[0].client_id);
        const allScopesSame = deliverables.every((deliverable) => deliverable.scope?.id === deliverables[0].scope?.id);
        const allConsultantsSame = deliverables.every(
          (deliverable) => deliverable.consultant?.id === deliverables[0].consultant?.id
        );
        const allDeliveryManagerSame = deliverables.every(
          (deliverable) => deliverable.delivery_manager_id === deliverables[0].delivery_manager?.id
        );
        const allContentsSame = deliverables.every((deliverable) => deliverable.content === deliverables[0].content);
        const allContent2Same = deliverables.every((deliverable) => deliverable.content2 === deliverables[0].content2);
        const allContent3Same = deliverables.every((deliverable) => deliverable.content3 === deliverables[0].content3);
        const allContent4Same = deliverables.every((deliverable) => deliverable.content4 === deliverables[0].content4);
        const allContent5Same = deliverables.every((deliverable) => deliverable.content5 === deliverables[0].content5);
        const allPurchaseOrderSame = deliverables.every(
          (deliverable) => deliverable.purchase_order === deliverables[0]?.purchase_order
        );
        setParamsForEdition(() => ({
          ...paramsForEdition,
          client: allClientsSame ? deliverables[0].client : undefined,
          scope: allScopesSame ? deliverables[0].scope : undefined,
          consultant: allConsultantsSame ? deliverables[0].consultant : undefined,
          delivery_manager: allDeliveryManagerSame ? deliverables[0].delivery_manager : undefined,
          purchase_order: allPurchaseOrderSame ? deliverables[0]?.purchase_order : undefined,
          purchase_order_id: allPurchaseOrderSame ? deliverables[0]?.purchase_order_id : undefined,
          content: allContentsSame ? deliverables[0].content : undefined,
          content2: allContent2Same ? deliverables[0].content2 : undefined,
          content3: allContent3Same ? deliverables[0].content3 : undefined,
          content4: allContent4Same ? deliverables[0].content4 : undefined,
          content5: allContent5Same ? deliverables[0].content5 : undefined,
        }));
      }

      setOrderWorkunits(() => deliverables);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [ids, baseLineOrder?.id]
  );

  useEffect(() => {
    getSelectedDeliverableByListId(totalworkunitData);
  }, [getSelectedDeliverableByListId, ids, baseLineOrder?.id]);

  useEffect(() => {
    if (multipleWorkunitSave) {
      submit('frompopup');
      dispatch(multipleWorkunitSaveAction(false));
    }
  }, [multipleWorkunitSave]);

  const submit = async (eOrIspopup?: any, maybeIspopup?: string) => {
    if (eOrIspopup && typeof eOrIspopup.preventDefault === 'function') {
      eOrIspopup.preventDefault();
    }
    if (orderWorkunits)
      try {
        // const updateWithReason: OrderWorkunit['id'][] = [];
        // const updateWithoutReason: OrderWorkunit['id'][] = [];
        dispatch(addLoadingAction('EditBLDeliveriable'));
        let cleanedObject: any = pickBy(paramsForEdition, (v) => v !== null);
        cleanedObject = Object.entries(cleanedObject).reduce((acc, [key, value]) => {
          if (value === 0 && key.endsWith('_id')) {
            const name = key.replace('_id', '');
            const fullNameKey = `${name}_fullname`;
            acc = {
              ...acc,
              [name]: null,
              [fullNameKey]: null,
              [key]: null,
            };
          } else {
            acc = {
              ...acc,
              [key]: value,
            };
          }
          return acc;
        }, {});
        if (paramsForEdition.consultant_id) {
          cleanedObject.consultant_id = paramsForEdition.consultant_id;
        }

        const clientData = cleanedObject.client
          ? {
              client: cleanedObject.client,
              client_fullname: `${cleanedObject.client.first_name} ${cleanedObject.client.last_name}`,
              client_id: cleanedObject.client.id,
            }
          : {};

        const consultantData = cleanedObject.consultant
          ? {
              consultant: cleanedObject.consultant,
              consultant_fullname: `${cleanedObject.consultant.first_name} ${cleanedObject.consultant.last_name}`,
              consultant_id: cleanedObject.consultant.id,
            }
          : {};

        const deliveryManagerData = cleanedObject.delivery_manager
          ? // Array.isArray(cleanedObject.delivery_manager) &&

            {
              delivery_manager_id: cleanedObject.delivery_manager?.id ?? null,
              delivery_manager: cleanedObject.delivery_manager,
              delivery_manager_fullname: `${cleanedObject?.delivery_manager?.last_name} ${cleanedObject?.delivery_manager?.first_name}`,
            }
          : {};

        const scopeData = cleanedObject.scope
          ? {
              scope: cleanedObject.scope,
              scope_id: cleanedObject.scope.id,
            }
          : {};

        // Initialize the object with the new type
        const customData: WorkUnitData = {};

        if (cleanedObject?.workunit_desc) {
          customData.workunit_desc = cleanedObject.workunit_desc;
        }

        if (cleanedObject?.workunit_name) {
          customData.workunit_name = cleanedObject.workunit_name;
        }

        if (cleanedObject?.workunit_reference) {
          customData.workunit_reference = cleanedObject.workunit_reference;
        }

        const baseLinePara = {
          page_typeid: 3,
          workunit_type: 'multiple',
          order_baseline_id: baseLineOrder?.id,
          source_order_id: Number(changeBaseline?.sourceOrder?.id),
        };
        const toSend: {
          ressources: number[];
          data: {
            consultant_id?: User['id'];
            consultant_fullname?: string;
            scope_id?: User['id'];
            forecast_date?: string;
            client_id?: User['id'];
            client_fullname?: string;
            order_baseline_id: number;
            source_order_id: number;
            delivery_manager_id?: User['id'];
            delivery_manager_fullname?: string;
            status: 1;
            content: string;
            content2: string;
            content3: string;
            content4: string;
            content5: string;
            purchase_order: PurchaseOrders;
            purchase_order_id: number;
            delivery_date: string;
            modification_reason: string;
          };
        } = {
          ressources: ids as unknown as number[],
          data: {
            order_baseline_id: Number(baseLineOrder?.id),
            ...(Object.keys(clientData).length > 0 && clientData),
            ...(Object.keys(consultantData).length > 0 && consultantData),
            ...(Object.keys(deliveryManagerData).length > 0 && deliveryManagerData),
            ...(Object.keys(scopeData).length > 0 && scopeData),
            ...(Object.keys(customData).length > 0 && customData),
            content: cleanedObject.content as string,
            content2: cleanedObject.content2 as string,
            content3: cleanedObject.content3 as string,
            content4: cleanedObject.content4 as string,
            content5: cleanedObject.content5 as string,
            purchase_order: cleanedObject.purchase_order as PurchaseOrders,
            purchase_order_id: Number(cleanedObject.purchase_order_id),
            source_order_id: Number(changeBaseline?.sourceOrder?.id),
            delivery_date: cleanedObject.delivery_date as string,
            forecast_date: cleanedObject.forecast_date as string,
            modification_reason: cleanedObject.modification_reason as string,
            status: 1,
          },
        };

        const res = await ChangeBaseLineAPIServices.bulkUpdate(
          baseLinePara,
          toSend.ressources,
          toSend.data as any,
          false
        );

        if (res) {
          dispatch(filterTotalFlagAction(true));
          dispatch(selectBLDeliverablesAction([]));
          if (maybeIspopup === 'frompopup') {
            dispatch(closeDialogAction('BaselineEditWorkunitCancel'));
            dispatch(closeDialogWithDataAction({ name: 'changeBaselineeditworkunitDialog', data: { data, type } }));
          } else {
            dispatch(closeDialogAction('changeBaselineeditworkunitDialog'));
          }
          if (maybeIspopup === 'frompopup') dispatch(closeDialogAction('BaselineEditWorkunitCancel'));
          dispatch(setSnackbarAction({ message: 'Saved', open: true, severity: 'success', duration: 500 }));
          dispatch(removeLoadingAction('EditBLDeliveriable'));
        }
      } catch (error) {
        dispatch(
          setSnackbarAction({
            message: 'Error while updating workunit',
            open: true,
            severity: 'error',
          })
        );
        dispatch(removeLoadingAction('EditBLDeliveriable'));
      } finally {
        dispatch(removeLoadingAction('EditBLDeliveriable'));
        // fetchDeliverables(baseLineOrder?.id, { current: null });
        // dispatch(closeDrawerAction());
      }
  };

  const handleDateChange = (date: Moment | null, name: string) => {
    if (date) {
      if (!date.isValid()) setDateValid(false);
      if (date.isValid()) setDateValid(true);
      setParamsForEdition({
        ...paramsForEdition,
        [name]: moment(date).format('YYYY-MM-DD'),
      });
    } else {
      setDateValid(true);
      setParamsForEdition({
        ...paramsForEdition,
        [name]: null,
      });
    }
  };

  const everyDeliverableIsNotFromQuote = isEveryDeliverableNotFromQuote(orderWorkunits);
  //  || state.selectedAllDeliverables;
  // const everyDeliverableIsCustom = isEveryDeliverableCustom(orderWorkunits) || state.selectedAllDeliverables;
  const everyDeliverableIsCustom = isEveryDeliverableCustom(orderWorkunits);
  useEffect(() => {
    const forecastDate: string | undefined = paramsForEdition.forecast_date as string | undefined;

    const isForecastDateChanged = (): boolean =>
      forecastDate !== undefined && orderWorkunits && orderWorkunits[0].forecast_date !== forecastDate;
    let isChangeDateTextareaOpen = false;

    if (everyDeliverableIsNotFromQuote) {
      isChangeDateTextareaOpen = false;
    } else if (orderWorkunits?.length === 1 && orderWorkunits) {
      isChangeDateTextareaOpen = isForecastDateChanged();
    } else if (orderWorkunits) {
      isChangeDateTextareaOpen = isForecastDateChanged();
    } else if (forecastDate !== undefined) {
      isChangeDateTextareaOpen = true;
    }

    setIsChangeDateTextareaOpen(() => isChangeDateTextareaOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [everyDeliverableIsNotFromQuote, orderWorkunits, paramsForEdition]);

  const getDefaultDisplayedValue = useCallback(
    (
      field: keyof Pick<
        OrderWorkunit,
        | 'workunit_name'
        | 'workunit_reference'
        | 'content'
        | 'delivery_date'
        | 'forecast_date'
        | 'content2'
        | 'workunit_desc'
        | 'purchase_order'
      >
    ): string => {
      if (paramsForEdition[field] !== undefined) {
        return paramsForEdition[field] ?? ''; // Explicitly handle null
      }

      if (orderWorkunits?.length === 1 && !paramsForEdition[field] && orderWorkunits[0][field])
        return orderWorkunits[0][field] ?? '';

      // if (paramsForEdition[field]) return paramsForEdition[field] ?? '';
      return '';
    },
    [orderWorkunits, paramsForEdition]
  );

  const handleSelectOptions = (e: SelectTypeOptions | null, property: PropertyNameOptions) => {
    if (e && e !== null && property === 'purchase_order') {
      const selectPurchaseOrder = purchaseOrder.find((po) => po.id === e.id);
      if (selectPurchaseOrder) {
        setParamsForEdition({
          ...paramsForEdition,
          purchase_order_id: selectPurchaseOrder?.id, // Just set the id (number)
          purchase_order: selectPurchaseOrder?.poref,
        });
      }
    }
  };
  const getDefaultScope = useCallback(() => {
    if (orderWorkunits?.length === 1) return orderWorkunits[0].scope;
    return undefined;
  }, [orderWorkunits]);

  const [autorization, setAutorization] = useState({
    everyDeliverableIsUnsubmitted: false,
    everyDeliverableCanHaveDateChange: false,
  });

  const everyDeliverableIsUnsubmitted = useCallback(
    () => isEveryDeliverableUnsubmitted(orderWorkunits),
    [orderWorkunits]
  );
  const everyDeliverableCanHaveDateChange = useCallback(
    () => canEveryDeliverableHaveDateChange(orderWorkunits),
    [orderWorkunits]
  );

  useEffect(() => {
    setAutorization(() => ({
      everyDeliverableIsUnsubmitted: everyDeliverableIsUnsubmitted(),
      everyDeliverableCanHaveDateChange: everyDeliverableCanHaveDateChange(),
    }));
  }, [everyDeliverableCanHaveDateChange, everyDeliverableIsUnsubmitted]);

  return (
    <form onSubmit={(e) => submit(e, 'false')} className="deliverables-edit-container">
      {orderWorkunits?.length === 1 && (
        <Box sx={{ backgroundColor: 'primary.light', borderRadius: '10px', p: 3, mb: 3 }}>
          <Box sx={{ mb: 1 }}>
            <Typography>{t('Scope_Name')}</Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontWeight: 500,
              }}
            >
              {orderWorkunits[0]?.scope?.name ? orderWorkunits[0]?.scope?.name : t('No_Scope')}
            </Typography>
          </Box>
          {orderWorkunits[0].workunit_reference && (
            <Box sx={{ mb: 1 }}>
              <Typography>{t('Reference_Of_Workunit')}</Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                {orderWorkunits[0].workunit_reference}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography>{t('Delivery_Date')}</Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontWeight: 500,
              }}
            >
              {orderWorkunits[0].delivery_date ||
                moment(orderWorkunits[0].forecast_date).format('DD-MM-YYYY') ||
                t('Not_Defined')}
            </Typography>
          </Box>
          {orderWorkunits[0].workunit_desc && (
            <Box sx={{ mb: 1 }}>
              <Typography>{t('Description_New_Workunit')}</Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                {orderWorkunits[0].workunit_desc}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {loadings.api ? (
        <Loader />
      ) : (
        <>
          {everyDeliverableIsCustom && (
            <>
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="mission_name">{t('Name')}</InputLabel>
                <TextField
                  name="workunit_name"
                  id="mission_name"
                  className="deliverables-input active-blue"
                  placeholder={t('Name_Deliverable')}
                  onChange={(e) => setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value })}
                  value={paramsForEdition.workunit_name ?? getDefaultDisplayedValue('workunit_name')}
                  required
                  fullWidth
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="mission_ref">{t('Reference')}</InputLabel>
                <TextField
                  name="workunit_reference"
                  id="mission_ref"
                  className="deliverables-input active-blue"
                  placeholder={t('Deliverable_Reference')}
                  onChange={(e) => setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value })}
                  required
                  value={paramsForEdition.workunit_reference ?? getDefaultDisplayedValue('workunit_reference') ?? ''}
                  fullWidth
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="workunit_desc">{t('Description')}</InputLabel>
                <TextField
                  name="workunit_desc"
                  id="workunit_desc"
                  rows={4}
                  className="active-blue background-grey no-border deliverables-input "
                  onChange={(e) => setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value })}
                  required
                  placeholder={t('Deliverable_Description')}
                  value={paramsForEdition.workunit_desc ?? getDefaultDisplayedValue('workunit_desc') ?? ''}
                  fullWidth
                  multiline
                />
              </Box>
            </>
          )}
          <Box sx={{ mb: 3 }}>
            <InputLabel htmlFor="content">{t('Content')}</InputLabel>
            <TextField
              name="content"
              id="content"
              rows={4}
              className="active-blue no-border deliverables-input "
              value={paramsForEdition.content ?? getDefaultDisplayedValue('content') ?? ''}
              required
              onChange={(e) =>
                setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value ? e.target.value : ' ' })
              }
              placeholder={t('Deliverable_Content')}
              multiline
              fullWidth
            />
          </Box>

          {paramsForEdition && (
            <ContentMultiple orderWorkunit={paramsForEdition as OrderWorkunit} onchange={setParamsForEdition} />
          )}

          {everyDeliverableIsCustom && (
            <LocalizationProvider dateAdapter={MomentUtils}>
              <Box sx={{ mb: 3 }}>
                <InputDate
                  label={t('Initial_Deliverable_Date')}
                  name="delivery_date"
                  id="delivery_date"
                  placeholder={dateFormat}
                  onchange={(name, date) => handleDateChange(date, name)}
                  value={
                    paramsForEdition.delivery_date ??
                    moment(getDefaultDisplayedValue('delivery_date')).format('YYYY-MM-DD')
                  }
                  min={baseLineOrder?.start_date ? moment(baseLineOrder.start_date).format('YYYY-MM-DD') : undefined}
                  max={baseLineOrder?.end_date ? moment(baseLineOrder.end_date).format('YYYY-MM-DD') : undefined}
                />
              </Box>
            </LocalizationProvider>
          )}
          <LocalizationProvider dateAdapter={MomentUtils}>
            <Box sx={{ mb: 3 }}>
              <InputDate
                label={t('New_Expected_Date')}
                name="forecast_date"
                id="forecast_date"
                placeholder={dateFormat}
                showError
                canEmpty
                // disabled={!everyDeliverableCanHaveDateChange()}
                onchange={(name, date) => handleDateChange(date, name)}
                value={paramsForEdition.forecast_date ?? getDefaultDisplayedValue('forecast_date')}
                // min={moment(state.order?.start_date).toString()}
                // max={moment(state.order?.end_date).add('1', 'year').toString()}
                min={baseLineOrder?.start_date ? moment(baseLineOrder.start_date).format('YYYY-MM-DD') : undefined}
                max={baseLineOrder?.end_date ? moment(baseLineOrder.end_date).format('YYYY-MM-DD') : undefined}
              />
            </Box>
            {isChangeDateTextareaOpen && (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="cancel-reason-input">{t('Comment your modifications')}</InputLabel>
                <TextField
                  name="cancel-reason"
                  id="cancel-reason-input"
                  rows={4}
                  value={updateDateReason ?? ''}
                  required
                  onChange={(e) => {
                    setUpdateDateReason(e.target.value);
                    setParamsForEdition(() => ({
                      ...paramsForEdition,
                      modification_reason: e.target.value,
                    }));
                  }}
                  placeholder={t('Enter your text here')}
                  multiline
                  fullWidth
                />
              </Box>
            )}
          </LocalizationProvider>

          <Box sx={{ mb: 3 }}>
            <Select
              label={t('Purchase_Order')}
              options={purchaseOrder}
              optionsLabels="reference"
              disabled={loadings.api > 0}
              required={false}
              handleSelectOptions={handleSelectOptions}
              selectedOption={purchaseOrder.find((po) => po?.id === paramsForEdition?.purchase_order_id) || null}
              propertyName="purchase_order"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            {scopes && (
              <Select
                options={scopes}
                selectedOption={paramsForEdition?.scope ?? getDefaultScope() ?? undefined}
                optionsLabels="name"
                label={t('Scopes')}
                handleSelectOptions={(opt) =>
                  setParamsForEdition({
                    ...paramsForEdition,
                    scope_id: opt?.id,
                    scope: opt as Scope | undefined,
                  })
                }
                propertyName="scope"
                required={false}
                // disabled={!(everyDeliverableIsNotFromQuote && autorization.everyDeliverableIsUnsubmitted)}
              />
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            {!userRoles.isConsultant && (
              <>
                {!consultants ? (
                  <Loader size={20} />
                ) : (
                  <>
                    <SelectUser
                      users={consultants}
                      selectedUser={paramsForEdition?.consultant ?? null}
                      onSelectUser={(value) => {
                        if (value) {
                          setParamsForEdition({
                            ...paramsForEdition,
                            consultant_id: value?.id,
                            consultant: value as User | undefined,
                          });
                        } else
                          setParamsForEdition({
                            ...paramsForEdition,
                            consultant_id: 0,
                            consultant: null,
                          });
                      }}
                      label={t('Consultant')}
                      required={ids?.length === 1}
                      // disabled={userRoles.isClient || !autorization.everyDeliverableIsUnsubmitted}
                    />
                  </>
                )}
              </>
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            {!accountables ? (
              <Loader size={20} />
            ) : (
              <>
                <SelectUser
                  users={accountables}
                  selectedUser={paramsForEdition?.delivery_manager as any}
                  onSelectUser={(value) => {
                    if (value)
                      setParamsForEdition({
                        ...paramsForEdition,
                        delivery_manager_ids: value?.id as any,
                        delivery_manager: value as any,
                      });
                    else
                      setParamsForEdition({
                        ...paramsForEdition,
                        delivery_manager_ids: [],
                        delivery_manager: [],
                      });
                  }}
                  label={t('Delivery_manager')}
                  required={ids?.length === 1}
                  // disabled={userRoles.isClient || !autorization.everyDeliverableIsUnsubmitted}
                />
              </>
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            {!customers ? (
              <Loader size={20} />
            ) : (
              <>
                <SelectUser
                  users={customers}
                  selectedUser={paramsForEdition?.client ?? null}
                  onSelectUser={(value) => {
                    if (value)
                      setParamsForEdition({
                        ...paramsForEdition,
                        client_id: value?.id,
                        client: value as User | undefined,
                      });
                    else
                      setParamsForEdition({
                        ...paramsForEdition,
                        client_id: 0,
                        client: null,
                      });
                  }}
                  label={t('Client')}
                  required={ids?.length === 1}
                  // disabled={userRoles.isClient || !autorization.everyDeliverableIsUnsubmitted}
                />
                {/* {userRoles.isClient ? null : (
                  <>
                    {!autorization.everyDeliverableIsUnsubmitted &&
                      t('To change the consultant, cancel the submission')}
                  </>
                )} */}
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingButton
              variant="contained"
              onClick={submit}
              loading={!!loadings?.EditBLDeliveriable}
              color="success"
              disabled={
                // (isChangeDateTextareaOpen && !updateDateReason) ||
                !dateValid ||
                // !paramsForEdition?.consultant ||
                // !paramsForEdition?.delivery_manager?.length ||
                // !paramsForEdition?.client ||
                !!loadings.api
              }
            >
              {t('Save')}
            </LoadingButton>
          </Box>
        </>
      )}
    </form>
  );
};

export default DeliverableMultipleEdit;
